// Pure conversion between the local SyncedTree shape and the flat
// per-collection rows the remote sync layer round-trips. No I/O.
//
// Field-name mapping rules (snake_case on the wire, camelCase locally):
//   avatar_emoji ↔ avatarEmoji
//   sort_index   ↔ sortIndex
//   accent_color ↔ color (ProfileTheme.accent_color is mirrored from
//                  the local `color` field; theme.accent stays inside the
//                  JSON `theme` blob, which round-trips as-is).
//   date_key     ↔ dateKey
//   tag          ↔ tag (string | null on both sides)
// `created`/`updated` are managed by Pocketbase and reconstructed locally
// from the row's `updated`. The local `createdAt` / `updatedAt` map to
// the same `updated` value when round-tripping (we don't separately track
// `created` on the wire — the conflict merge only reads `updated`).

import type {
  DailyProgress,
  Profile,
  ProfileTheme,
  Routine,
  SyncedTree,
  Task,
} from '../../lib/types';
import type { RemoteRecord } from './types';

// ---- Wire row shapes ----

export interface ProfileRow extends RemoteRecord {
  name: string;
  avatar_emoji: string;
  theme: ProfileTheme;
  accent_color: string;
  sort_index: number;
}

// v3: routines are family-scoped only — no profile FK. Assignment to a
// child lives in the profile_routines junction. See tasks/43 §3-4.
export interface RoutineRow extends RemoteRecord {
  name: string;
  emoji: string;
  sort_index: number;
}

// v3 junction row: which routines a profile shows and in what order.
export interface ProfileRoutineRow extends RemoteRecord {
  profile: string;
  routine: string;
  sort_index: number;
}

export interface TaskRow extends RemoteRecord {
  routine: string;
  name: string;
  emoji: string;
  tag: string | null;
  sort_index: number;
}

export interface ProgressRow extends RemoteRecord {
  profile: string;
  routine: string;
  date_key: string;
  completed: boolean[];
}

export interface RemoteTree {
  profiles: ProfileRow[];
  routines: RoutineRow[];
  tasks: TaskRow[];
  profileRoutines: ProfileRoutineRow[];
}

// ---- Tree → wire ----

// Junction row id: deterministic from (profile, routine) so both devices
// produce the same id for the same edge. PocketBase requires exactly 15
// [a-z0-9] chars — same hashing scheme as `progressRowId` below.
export function profileRoutineRowId(profileId: string, routineId: string): string {
  const input = `${profileId}:${routineId}`;
  let h = 0xcbf29ce484222325n;
  for (let i = 0; i < input.length; i++) {
    h ^= BigInt(input.codePointAt(i)!);
    h = BigInt.asUintN(64, h * 0x100000001b3n);
  }
  return h.toString(36).padStart(15, '0').slice(0, 15);
}

export function treeToRemote(tree: SyncedTree, familyId: string): RemoteTree {
  const profiles: ProfileRow[] = [];
  const routines: RoutineRow[] = [];
  const tasks: TaskRow[] = [];
  const profileRoutines: ProfileRoutineRow[] = [];

  for (const profile of tree.profiles) {
    profiles.push(profileToRow(profile, familyId));
    // Junction rows carry the per-profile order via `sort_index`.
    profile.routineIds.forEach((routineId, i) => {
      profileRoutines.push(
        profileRoutineToRow(profile.id, routineId, i, familyId, profile.updatedAt),
      );
    });
  }
  for (const routine of tree.routines) {
    routines.push(routineToRow(routine, familyId));
    for (const task of routine.tasks) {
      tasks.push(taskToRow(task, routine.id, familyId));
    }
  }

  return { profiles, routines, tasks, profileRoutines };
}

export function profileToRow(profile: Profile, familyId: string): ProfileRow {
  return {
    id: profile.id,
    family: familyId,
    updated: profile.updatedAt,
    name: profile.name,
    avatar_emoji: profile.avatarEmoji,
    theme: profile.theme,
    accent_color: profile.color,
    sort_index: profile.sortIndex,
  };
}

export function routineToRow(routine: Routine, familyId: string): RoutineRow {
  return {
    id: routine.id,
    family: familyId,
    updated: routine.updatedAt,
    name: routine.name,
    emoji: routine.emoji,
    sort_index: routine.sortIndex,
  };
}

export function profileRoutineToRow(
  profileId: string,
  routineId: string,
  sortIndex: number,
  familyId: string,
  updated: string,
): ProfileRoutineRow {
  return {
    id: profileRoutineRowId(profileId, routineId),
    family: familyId,
    profile: profileId,
    routine: routineId,
    sort_index: sortIndex,
    updated,
  };
}

export function taskToRow(task: Task, routineId: string, familyId: string): TaskRow {
  return {
    id: task.id,
    family: familyId,
    routine: routineId,
    updated: task.updatedAt,
    name: task.name,
    emoji: task.emoji,
    tag: task.tag,
    sort_index: task.sortIndex,
  };
}

// PocketBase 0.22 enforces custom IDs to be exactly 15 alphanumeric
// [a-z0-9] characters (models.DefaultIdLength). We hash the composite
// key (profile + routine + date) with FNV-1a 64-bit to produce a
// deterministic 15-char ID so the same (profile, routine, day) always
// maps to the same remote row.
export function progressRowId(profileId: string, routineId: string, dateKey: string): string {
  const input = `${profileId}:${routineId}:${dateKey}`;
  let h = 0xcbf29ce484222325n;
  for (let i = 0; i < input.length; i++) {
    h ^= BigInt(input.codePointAt(i)!);
    h = BigInt.asUintN(64, h * 0x100000001b3n);
  }
  return h.toString(36).padStart(15, '0').slice(0, 15);
}

export function progressToRemote(
  daily: DailyProgress,
  familyId: string,
  routineId: string,
  rowId: string = progressRowId(daily.profileId, routineId, daily.dateKey),
): ProgressRow {
  const completed = daily.routines[routineId] ?? [];
  return {
    id: rowId,
    family: familyId,
    profile: daily.profileId,
    routine: routineId,
    date_key: daily.dateKey,
    completed,
    updated: daily.updatedAt,
  };
}

// ---- Wire → tree ----

function byRemoteSortIndex<T extends { sort_index: number; updated: string }>(a: T, b: T): number {
  if (a.sort_index !== b.sort_index) return a.sort_index - b.sort_index;
  if (a.updated < b.updated) return -1;
  if (a.updated > b.updated) return 1;
  return 0;
}

export function applyRemoteToTree(remote: RemoteTree): Pick<SyncedTree, 'profiles' | 'routines'> {
  // Group junction rows by profile so we can rebuild each profile's
  // routineIds in the server-published order.
  const junctionByProfile = new Map<string, ProfileRoutineRow[]>();
  for (const j of remote.profileRoutines) {
    const list = junctionByProfile.get(j.profile);
    if (list) list.push(j);
    else junctionByProfile.set(j.profile, [j]);
  }
  const tasksByRoutine = new Map<string, TaskRow[]>();
  for (const t of remote.tasks) {
    const list = tasksByRoutine.get(t.routine);
    if (list) list.push(t);
    else tasksByRoutine.set(t.routine, [t]);
  }

  const sortedProfiles = remote.profiles.slice().sort(byRemoteSortIndex);
  const profiles: Profile[] = sortedProfiles.map((row) => {
    const junctionRows = (junctionByProfile.get(row.id) ?? []).slice().sort(byRemoteSortIndex);
    return {
      id: row.id,
      name: row.name,
      avatarEmoji: row.avatar_emoji,
      theme: row.theme,
      color: row.accent_color,
      sortIndex: row.sort_index,
      createdAt: row.updated,
      updatedAt: row.updated,
      routineIds: junctionRows.map((j) => j.routine),
    };
  });

  const routines: Routine[] = remote.routines
    .slice()
    .sort(byRemoteSortIndex)
    .map<Routine>((rr) => ({
      id: rr.id,
      name: rr.name,
      emoji: rr.emoji,
      sortIndex: rr.sort_index,
      createdAt: rr.updated,
      updatedAt: rr.updated,
      tasks: (tasksByRoutine.get(rr.id) ?? [])
        .slice()
        .sort(byRemoteSortIndex)
        .map<Task>((tr) => ({
          id: tr.id,
          name: tr.name,
          emoji: tr.emoji,
          tag: tr.tag,
          sortIndex: tr.sort_index,
          createdAt: tr.updated,
          updatedAt: tr.updated,
        })),
    }));

  return { profiles, routines };
}

// Reconstruct a per-(profile, dateKey) DailyProgress from the remote
// rows. Caller filters the rows to the relevant profile/day before
// calling. Returns null when no rows match (caller decides whether to
// treat that as an empty progress doc).
export function applyRemoteToProgress(remoteRows: ProgressRow[]): DailyProgress | null {
  if (remoteRows.length === 0) return null;
  const first = remoteRows[0]!;
  const routines: Record<string, boolean[]> = {};
  let latestUpdated = first.updated;
  for (const row of remoteRows) {
    routines[row.routine] = row.completed;
    if (row.updated > latestUpdated) latestUpdated = row.updated;
  }
  return {
    profileId: first.profile,
    dateKey: first.date_key,
    routines,
    updatedAt: latestUpdated,
  };
}
